import React, { useState, useEffect } from 'react';
import { Link, Outlet } from '@tanstack/react-router';
import { PlusIcon, CubeIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { get } from '../api';
import AiStudioPaywall from '../components/AiStudioPaywall';

export default function AiStudioLayout() {
  const [credits, setCredits] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const data = await get('/api/1/user/usage');
        setCredits(data.ai_studio_credits);
        setIsSubscribed(data.has_active_subscription);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCredits();
    
    // Listen for credit updates
    const handleCreditUpdate = () => fetchCredits();
    window.addEventListener('credits-updated', handleCreditUpdate);
    return () => window.removeEventListener('credits-updated', handleCreditUpdate);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (isSubscribed === false) {
    return <AiStudioPaywall />;
  }

  return (
    <div className="flex h-full bg-background text-foreground font-sans selection:bg-indigo-500/30">
      {/* Secondary Sidebar */}
      <div className="w-64 bg-background/80 backdrop-blur-xl border-r border-border/40 flex flex-col shrink-0 z-20">
        <div className="p-6 border-b border-border/40">
          <h1 className="text-lg font-bold tracking-widest uppercase bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            AI Studio
          </h1>
        </div>
        <nav className="flex-grow p-4 space-y-2">
          <Link
            to="/app/ai-studio/create"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group"
            activeProps={{
              className: "bg-foreground/5 text-foreground shadow-sm ring-1 ring-border/50"
            }}
            inactiveProps={{
              className: "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
            }}
          >
            <PlusIcon className="w-4 h-4" />
            GENERATE
          </Link>
          <Link
            to="/app/ai-studio/models"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group"
            activeProps={{
              className: "bg-foreground/5 text-foreground shadow-sm ring-1 ring-border/50"
            }}
            inactiveProps={{
              className: "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
            }}
          >
            <CubeIcon className="w-4 h-4" />
            MODELS
          </Link>
          <Link
            to="/app/ai-studio/billing"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group"
            activeProps={{
              className: "bg-foreground/5 text-foreground shadow-sm ring-1 ring-border/50"
            }}
            inactiveProps={{
              className: "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
            }}
          >
            <CreditCardIcon className="w-4 h-4" />
            BILLING
          </Link>
        </nav>

        <div className="p-4 border-t border-border/40">
          <div className={`rounded-xl p-5 backdrop-blur-sm transition-colors ${credits !== null && credits < 50 ? 'bg-red-500/5 border border-red-500/20' : 'bg-foreground/5 border border-border/40'}`}>
            <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Credits</p>
                {credits !== null && credits < 50 && (
                    <Link to="/app/ai-studio/billing" className="text-[10px] font-bold uppercase tracking-wider text-red-500 hover:text-red-400 flex items-center gap-1">
                        Top Up <span className="text-xs">→</span>
                    </Link>
                )}
            </div>
            
            <div className="flex items-baseline gap-1 mb-3">
              <span className={`text-4xl font-light tracking-tighter ${credits !== null && credits < 50 ? 'text-red-500' : 'text-foreground'}`}>
                {credits !== null ? credits : '...'}
              </span>
              <span className="text-xs text-muted-foreground font-mono">/ mo</span>
            </div>

            <div className="w-full bg-background/50 h-1.5 rounded-full overflow-hidden">
                <div 
                    className={`h-full rounded-full transition-all duration-500 ${credits !== null && credits < 50 ? 'bg-red-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`} 
                    style={{ width: `${Math.min((credits || 0) / 100 * 100, 100)}%` }}
                />
            </div>
          </div>
        </div>
        </div>

      <div className="flex-grow bg-background-soft overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
